import React, { useState } from "react";
import { supabase } from "../config/supabase";

interface AdminInvitationsProps {
  user: any;
}

const AdminInvitations: React.FC<AdminInvitationsProps> = ({ user }) => {
  const [email, setEmail] = useState("");
  // This tool now only handles doctor promotion/invitation
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    try {
      if (!email.trim()) {
        setStatus(
          "Please enter an existing user's email to promote to Doctor."
        );
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setStatus("Please enter a valid email address.");
        setLoading(false);
        return;
      }

      // Promote existing user via Postgres RPC (no Edge Functions)
      const { data, error } = await supabase.rpc("promote_user_to_doctor", {
        p_email: email.trim().toLowerCase(),
      });
      if (error) throw error;
      const ok = (data && (data as any).success) || data === true;
      const message =
        (data && (data as any).message) ||
        "User promoted to doctor successfully.";
      if (ok) {
        setStatus(message);
        setEmail("");
      } else {
        setStatus(
          (data && (data as any).error) || "Failed to promote user to doctor."
        );
      }
    } catch (err: any) {
      console.error("Admin invitation error:", err);
      setStatus(err.message || "Operation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl p-6 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.25)] dark:bg-gray-800 dark:shadow-[0_12px_50px_rgba(0,0,0,0.6)] dark:ring-1 dark:ring-black/40">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Send Registration Invitations
      </h2>

      <div className="max-w-md">
        <form onSubmit={handleSendInvitation} className="space-y-4">
          <div>
            <label
              htmlFor="invitation-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              Email Address
            </label>
            <input
              id="invitation-email"
              type="email"
              maxLength={254}
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 dark:placeholder-gray-500 text-base bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Only doctor actions supported here */}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Sending invitation...
              </div>
            ) : (
              "Send Invitation"
            )}
          </button>
        </form>

        {status && (
          <div
            className={`p-3 rounded-lg text-sm font-medium mt-4 ${
              status.includes("successfully")
                ? "bg-green-50 text-green-800 border border-green-200 dark:bg-green-900 dark:text-green-200"
                : "bg-red-50 text-red-800 border border-red-200 dark:bg-red-900 dark:text-red-200"
            }`}
          >
            {status}
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
            How it works:
          </h3>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>• Enter an existing user's email to promote them to Doctor</li>
            <li>
              • If the user does not exist, a Doctor account is created
              immediately
            </li>
            <li>
              • No emails are sent; credentials can be shared manually if a new
              account is created
            </li>
            <li>
              • Doctors have access per your existing RLS policies via
              `doctor_user_access`
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminInvitations;
